import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventDetailsTab = ({ event, venues, onSave, onDuplicate, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    event_date: '',
    end_date: '',
    doors_open: '',
    venue_id: '',
    category_id: '',
    max_capacity: '',
    min_age: '',
    status: 'draft',
    is_featured: false,
    featured_image_url: '',
    gallery_images: [],
    tags: [],
    terms_conditions: '',
    cancellation_policy: ''
  });

  const [errors, setErrors] = useState({});
  const [isDraft, setIsDraft] = useState(true);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event?.title || '',
        description: event?.description || '',
        short_description: event?.short_description || '',
        event_date: event?.event_date ? new Date(event.event_date)?.toISOString()?.slice(0, 16) : '',
        end_date: event?.end_date ? new Date(event.end_date)?.toISOString()?.slice(0, 16) : '',
        doors_open: event?.doors_open ? new Date(event.doors_open)?.toISOString()?.slice(0, 16) : '',
        venue_id: event?.venue_id || '',
        category_id: event?.category_id || '',
        max_capacity: event?.max_capacity?.toString() || '',
        min_age: event?.min_age?.toString() || '0',
        status: event?.status || 'draft',
        is_featured: event?.is_featured || false,
        featured_image_url: event?.featured_image_url || '',
        gallery_images: event?.gallery_images || [],
        tags: event?.tags || [],
        terms_conditions: event?.terms_conditions || '',
        cancellation_policy: event?.cancellation_policy || ''
      });
      setIsDraft(event?.status === 'draft');
    }
  }, [event]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData?.event_date) {
      newErrors.event_date = 'Event date is required';
    }

    if (!formData?.venue_id) {
      newErrors.venue_id = 'Venue selection is required';
    }

    if (formData?.max_capacity && isNaN(parseInt(formData?.max_capacity))) {
      newErrors.max_capacity = 'Max capacity must be a valid number';
    }

    if (formData?.min_age && isNaN(parseInt(formData?.min_age))) {
      newErrors.min_age = 'Min age must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSave = async (status = 'draft') => {
    if (!validateForm()) {
      return;
    }

    const saveData = {
      ...formData,
      status,
      max_capacity: formData?.max_capacity ? parseInt(formData?.max_capacity) : null,
      min_age: formData?.min_age ? parseInt(formData?.min_age) : 0,
      event_date: new Date(formData.event_date)?.toISOString(),
      end_date: formData?.end_date ? new Date(formData.end_date)?.toISOString() : null,
      doors_open: formData?.doors_open ? new Date(formData.doors_open)?.toISOString() : null
    };

    await onSave?.(saveData);
  };

  const handleDuplicate = () => {
    if (event?.id) {
      onDuplicate?.(event?.id);
    }
  };

  const venueOptions = [
    { value: '', label: 'Select a venue' },
    ...(venues?.map(venue => ({
      value: venue?.id,
      label: `${venue?.name} - ${venue?.city}`
    })) || [])
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {event ? 'Edit Event' : 'Create New Event'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {event ? 'Update event information and settings' : 'Set up your event with comprehensive details'}
          </p>
        </div>
        <div className="flex gap-3">
          {event && (
            <Button
              variant="outline"
              onClick={handleDuplicate}
              iconName="Copy"
            >
              Duplicate
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave('draft')}
            loading={loading}
          >
            Save Draft
          </Button>
          <Button
            variant="default"
            onClick={() => handleSave('published')}
            loading={loading}
          >
            {isDraft ? 'Publish Event' : 'Update Event'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            
            <Input
              label="Event Title"
              placeholder="Enter event title"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <Input
              label="Short Description"
              placeholder="Brief description for listings"
              value={formData?.short_description}
              onChange={(e) => handleInputChange('short_description', e?.target?.value)}
              maxLength={150}
            />

            <Input
              label="Full Description"
              placeholder="Detailed event description"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              multiline
              rows={4}
            />
          </div>

          {/* Date & Time */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Date & Time</h3>
            
            <Input
              label="Event Start Date & Time"
              type="datetime-local"
              value={formData?.event_date}
              onChange={(e) => handleInputChange('event_date', e?.target?.value)}
              error={errors?.event_date}
              required
            />

            <Input
              label="Event End Date & Time"
              type="datetime-local"
              value={formData?.end_date}
              onChange={(e) => handleInputChange('end_date', e?.target?.value)}
            />

            <Input
              label="Doors Open Time"
              type="datetime-local"
              value={formData?.doors_open}
              onChange={(e) => handleInputChange('doors_open', e?.target?.value)}
            />
          </div>

          {/* Venue & Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Venue & Settings</h3>
            
            <Select
              label="Venue"
              value={formData?.venue_id}
              onChange={(e) => handleInputChange('venue_id', e?.target?.value)}
              options={venueOptions}
              error={errors?.venue_id}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Max Capacity"
                type="number"
                placeholder="Maximum attendees"
                value={formData?.max_capacity}
                onChange={(e) => handleInputChange('max_capacity', e?.target?.value)}
                error={errors?.max_capacity}
              />

              <Input
                label="Minimum Age"
                type="number"
                placeholder="0"
                value={formData?.min_age}
                onChange={(e) => handleInputChange('min_age', e?.target?.value)}
                error={errors?.min_age}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Status & Visibility */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Status & Visibility</h3>
            
            <Select
              label="Event Status"
              value={formData?.status}
              onChange={(e) => handleInputChange('status', e?.target?.value)}
              options={statusOptions}
            />

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData?.is_featured}
                onChange={(e) => handleInputChange('is_featured', e?.target?.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Feature this event</span>
            </label>
          </div>

          {/* Media */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Media</h3>
            
            <Input
              label="Featured Image URL"
              placeholder="https://example.com/image.jpg"
              value={formData?.featured_image_url}
              onChange={(e) => handleInputChange('featured_image_url', e?.target?.value)}
            />

            <div className="text-sm text-muted-foreground">
              Upload featured image for event listings and hero section
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Tags & Categories</h3>
            
            <Input
              label="Tags"
              placeholder="Enter tags separated by commas"
              value={formData?.tags?.join(', ') || ''}
              onChange={(e) => handleInputChange('tags', e?.target?.value?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag))}
            />
          </div>

          {/* Policies */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Policies</h3>
            
            <Input
              label="Terms & Conditions"
              placeholder="Event terms and conditions"
              value={formData?.terms_conditions}
              onChange={(e) => handleInputChange('terms_conditions', e?.target?.value)}
              multiline
              rows={3}
            />

            <Input
              label="Cancellation Policy"
              placeholder="Cancellation and refund policy"
              value={formData?.cancellation_policy}
              onChange={(e) => handleInputChange('cancellation_policy', e?.target?.value)}
              multiline
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsTab;